import React, { useEffect, useRef, useState } from "react";
import ReactFullpage from "@fullpage/react-fullpage";
import "fullpage.js/dist/fullpage.css";
import MeetTheTeamSlide from "./MeetTheTeamSlide";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import Footer from "./Footer";

const SLIDES = [
  {
    title: "Powered by Technology",
    body: "We leverage cutting-edge property management technology to deliver a seamless experience for both residents and owners.",
    features: [
      "Pay Rent",
      "Submit & track maintenance requests",
      "View & download lease documents",
      "Communicate with property manager",
      "Electronic signatures",
    ],
    cta: { label: "Manage My Property", href: "#contactUs" },
  },
  {
    title: "Owner Transparency",
    body: "No more guessing - Instantly access your statements, income & expenses.",
    features: [
      "Monthly owner statements",
      "Expense receipts & approvals",
      "1099 & year-end packages",
      "Live portfolio performance",
    ],
    cta: { label: "See Owner Portal", href: "/owner-portal" },
  },
  {
    title: "Resident Experience",
    body: "Exceptional resident experiences reduce vacancies and turnover - we make that our focus.",
    features: [
      "Mobile portal & autopay",
      "Smart maintenance triage",
      "Proactive status updates",
      "Move-in/move-out inspections",
    ],
    cta: { label: "Explore Resident Tools", href: "/resident-tools" },
  },
  {
    title: "Our Maintenance Process",
    body: "We vet and partner trusted professionals to handle anything from dripping faucets to a full scale remodel.",
    features: [
      "24 hr maintenance",
      "Vetted & insured vendors",
      "Photo & invoice tracking",
      "Frequent unit inspections",
    ],
    cta: { label: "Submit Maintenance Request", href: "/maintenance" },
  },
  {
    title: "Leasing & Marketing",
    body: "Your property gets maximum exposure through over a dozen professional listings, including Zillow, Redfin, and Apartments.com. From showings to screenings, we handle it all.",
    features: [
      "Professional property listings",
      "Tenant screening & background checks",
      "Online applications & e-sign leasing",
      "Automated rent-ready coordination",
    ],
    cta: { label: "View Available Rentals", href: "/rentals" },
  },
  {
    title: "Financial Reporting",
    body: "Through the Owner Portal, combined with monthly statements you're always up to date with your property's performance.",
    features: [
      "Automated rent collection",
      "Expense categorization & reports",
      "Owner distributions & tracking",
      "Sync with QuickBooks Online",
    ],
    cta: { label: "View Financial Tools", href: "/financials" },
  },
];

const sectionBg = "bg-gradient-to-r from-[#1E2A38] via-[#26394C] to-[#3D5E7A]";
// const sectionBg = "bg-[#1E2A38]";
export function FeaturesFullpageBlock() {
  const location = useLocation();

  useEffect(() => {
    document.title = "Owners – Heartland Property Solutions";
  }, []);

  const phrases = [
    "We'll collect the rent.",
    "We'll fix the sink.",
    "We'll show the property.",
  ];

  const [index, setIndex] = useState(0);

  // Rotate phrases every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % phrases.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [phrases.length]);

  const apiRef = useRef(null);
  const insideRef = useRef(true); // track if we’re inside the block

  const SPEED = 450;

  return (
    <div>
      <ReactFullpage
        //fullpage options
        licenseKey={"N1CG6-QB45H-EKCRI-0HXQ7-OWZAL"}
        anchors={["home", "features", "team", "contactUs"]}
        verticalCentered={true}
        slidesNavigation={true} // ✅ enables dots under horizontal scroll
        slidesNavPosition="bottom" // or 'top'
        credits={{ enabled: false }}
        lockAnchors={true}
        scrollingSpeed={200} /* Options here */
        scrollHorizontally={true} /* Because we are using the extension */
        recordHistory={false}
        responsiveWidth={760}
        responsiveHeight={600}
        responsiveSlides={true}
        render={({ state, fullpageApi }) => {
          return (
            <ReactFullpage.Wrapper>
              <div className="section bg-[#1E2A38]">
                {/* Background image: fills section height, anchored to the right, ignores container padding */}
                <img
                  src="/blueprint-of-apartment-complex.png"
                  alt="Street view of an apartment complex blueprint."
                  aria-hidden="true"
                  className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover"
                  onLoad={(e) =>
                    e.currentTarget.classList.remove(
                      "opacity-0",
                      "translate-x-10"
                    )
                  }
                />

                {/* Optional subtle fade so text is always readable on top of the blueprint */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#1E2A38] via-[#1E2A38]/70 to-transparent" />

                {/* Content */}
                <section className="relative z-10">
                  <div
                    className="mt-20 mx-auto px-6 lg:px-10
                max-w-[1200px] md:max-w-[1280px]
                lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                  >
                    <div className="flex items-center py-14 lg:py-18">
                      {/* Left-only content; no right column needed since image is the background */}
                      <div className="max-w-[800px]">
                        <h1 className="text-5xl md:text-6xl xl:text-7xl font-extrabold text-white leading-[1.08] tracking-tight">
                          <AnimatePresence mode="wait">
                            <motion.span
                              key={phrases[index]}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              transition={{ duration: 0.6 }}
                              className="block"
                            >
                              {phrases[index]}
                            </motion.span>
                          </AnimatePresence>
                          <span className="text-[#d1d1d1] block">
                            You keep investing.
                          </span>
                        </h1>

                        <p className="mt-6 text-lg md:text-lg text-white/85">
                          Enjoy the peace of mind that comes with <br /> full
                          service Property Management.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              if (window.fullpage_api) {
                                window.fullpage_api.moveTo("contactUs"); // ID must match section anchor
                              }
                            }}
                            href="#contactUs"
                            className="inline-flex items-center justify-center rounded-md border border-black bg-[#F9DC5C] px-6 py-4 text-md font-semibold text-black hover:translate-y-[1px] transition"
                          >
                            Manage My Property
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <div className="section bg-gradient-to-b from-[#1E2A38] via-[#26394C] to-[#3D5E7A]">
                {SLIDES.map((s, i) => (
                  <div
                    key={i}
                    className="mx-auto slide flex flex-col md:flex-row items-center w-full h-screen overflow-hidden md:overflow-visible px-6 lg:px-12"
                  >
                    {/* LEFT */}
                    <div className="flex-1 flex items-center p-6 md:p-10">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={s.title}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.6 }}
                          className="text-center md:text-left max-w-xl"
                        >
                          <h2 className="text-4xl font-extrabold text-white mb-4">
                            {s.title}
                          </h2>
                          <p className="text-white/90">{s.body}</p>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* RIGHT */}
                    <div className="flex-[2] flex items-center justify-center px-6 md:px-10">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={s.title + "-features"}
                          initial={{ rotateY: 90, opacity: 0 }}
                          animate={{ rotateY: 0, opacity: 1 }}
                          exit={{ rotateY: -90, opacity: 0 }}
                          transition={{ duration: 0.8, ease: "easeInOut" }}
                          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 w-full"
                        >
                          {s.features.map((f, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-3 rounded-md bg-white/5 p-5 hover:bg-white/10 transition"
                            >
                              <CheckCircleIcon className="h-6 w-6 text-[#F9DC5C] mt-1" />
                              <h3 className="text-white text-md font-bold">
                                {f}
                              </h3>
                            </div>
                          ))}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* CTA */}
                    <div className="w-full p-6 md:w-auto md:p-10">
                      {s.cta && (
                        <a
                          href={s.cta.href}
                          className="w-full md:w-auto block md:inline-block rounded-md bg-[#F9DC5C] px-6 py-3 text-sm font-semibold text-black hover:brightness-95 transition text-center"
                        >
                          {s.cta.label}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <section id="team" className="section w-full bg-[#FCFBF6]">
                <div className="mx-auto max-w-[1200px] flex flex-col items-center justify-center px-6 py-20">
                  <h2 className="text-4xl font-extrabold text-center text-brand-black mb-12">
                    Meet your new team
                  </h2>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-12">
                    {/* Joseph */}
                    <div className="flex flex-col items-center text-center">
                      <img
                        src="/joe-moats-pro.png"
                        alt="Joseph Moats"
                        className="w-48 h-48 sm:w-56 sm:h-56 object-cover rounded-full"
                      />
                      <p className="font-semibold text-brand-black text-lg mt-4">
                        Joseph Moats
                      </p>
                      <span className="text-brand-black/70 text-sm">
                        Owner & Operator
                      </span>
                    </div>

                    {/* Patty */}
                    <div className="flex flex-col items-center text-center">
                      <img
                        src="/patty-thomas-profile-pic.jpg"
                        alt="Joseph Moats"
                        className="w-48 h-48 sm:w-56 sm:h-56 object-cover rounded-full"
                      />
                      <p className="font-semibold text-brand-black text-lg mt-4">
                        Patty Thomas
                      </p>
                      <span className="text-brand-black/70 text-sm">
                        Accounts Payable / Receivable Specialist
                      </span>
                    </div>

                    {/* Dan */}
                    <div className="flex flex-col items-center text-center">
                      <img
                        src="/henry-betty-profile-pic.png"
                        alt="Henry Betty"
                        className="w-48 h-48 sm:w-56 sm:h-56 object-cover rounded-full"
                      />
                      <p className="font-semibold text-brand-black text-lg mt-4">
                        Henry Betty
                      </p>
                      <span className="text-brand-black/70 text-sm">
                        Leasing Manager
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="section w-full bg-[#1E2A38]">
                <div
                  className="mx-auto px-3 lg:px-10
      max-w-[1200px] md:max-w-[1280px]
      lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                >
                  <div className="px-3 py-8 md:p-20 md:h-[42rem] 2xl:h-[52rem] md:mt-16 flex flex-col md:flex-row items-center items-stretch gap-8">
                    {/* LEFT: Heading + List */}
                    <div className="flex-1 flex flex-col justify-center text-white">
                      <h2 className="text-4xl font-extrabold leading-tight md:text-6xl">
                        Want to rent out
                        <br />{" "}
                        <span className="text-[#d1d1d1]">your property?</span>
                      </h2>

                      <p className="mt-6 text-white/80 max-w-lg">
                        Let Heartland take the stress out of investing.
                        <br />
                        Contact us today to get started.
                      </p>

                      <ul className="mt-6 space-y-5">
                        {[
                          "Competitive Pricing",
                          "Leasing Services & Tenant Screening",
                          "Complete Financial Reporting",
                          "In Person Showings",
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-3">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#F9DC5C]">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-[#1E2A38]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </span>
                            <span className="text-md font-medium">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* RIGHT: Form */}
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-md flex flex-col justify-center">
                        <form
                          action="https://formsubmit.co/bpotsmiket@gmail.com"
                          method="POST"
                          className="space-y-3"
                        >
                          {/* Redirect after submission */}
                          <input
                            type="hidden"
                            name="_next"
                            value="http://localhost:3001/thank-you"
                          />

                          {/* Disable captcha (optional) */}
                          <input type="hidden" name="_captcha" value="true" />

                          {/* First Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              First Name
                            </label>
                            <input
                              type="text"
                              name="firstName"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Last Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name
                            </label>
                            <input
                              type="text"
                              name="lastName"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Phone Number */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number
                            </label>
                            <input
                              type="text"
                              name="phone"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Work Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Work Email
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Location */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Location
                            </label>
                            <input
                              type="text"
                              name="location"
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Units */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Number of Units
                            </label>
                            <input
                              type="number"
                              name="units"
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Submit Button */}
                          <button
                            type="submit"
                            className="w-full rounded-md bg-[#F9DC5C] py-3 font-semibold text-black 
                hover:brightness-95 transition"
                          >
                            Let’s Talk!
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <Footer />
            </ReactFullpage.Wrapper>
          );
        }}
      />
    </div>
  );
}
